import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.NX_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NX_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NX_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NX_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NX_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NX_PUBLIC_FIREBASE_APP_ID,
};

const vapidKey = process.env.NX_PUBLIC_FIREBASE_VAPID_KEY;

export const isPushNotificationsConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId &&
    vapidKey
);

// Lazy singletons for Firebase instances
let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;

export async function initializeFirebaseMessaging(): Promise<void> {
  if (!isPushNotificationsConfigured) return;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return;

  // NOTE(dave|SIN-2511|2026-03-30): Pre-register the service worker because Firebase's
  // internal registration times out on slow dev servers. Uses baseURI so the path resolves
  // correctly in subdirectory deployments (e.g. /1392/).
  const swUrl = new URL('firebase-messaging-sw.js', document.baseURI).href;
  const serviceWorkerRegistration = await navigator.serviceWorker.register(swUrl);
  await navigator.serviceWorker.ready;

  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseConfig);
  }
  if (!messaging) {
    messaging = getMessaging(firebaseApp);

    // NOTE(dave|SIN-2511|2026-03-30): FCM delivers via onMessage when the page is focused,
    // bypassing the service worker's onBackgroundMessage handler.
    onMessage(messaging, (payload) => {
      const { title, body } = payload.notification ?? {};
      serviceWorkerRegistration.showNotification(title ?? 'New message', {
        body: body ?? '',
        data: payload.data,
      });
    });
  }
}

export async function requestFcmToken(): Promise<string | null> {
  if (!isPushNotificationsConfigured) return null;

  await initializeFirebaseMessaging();

  if (!messaging) return null;

  const swUrl = new URL('firebase-messaging-sw.js', document.baseURI).href;
  const serviceWorkerRegistration = await navigator.serviceWorker.register(swUrl);

  return getToken(messaging, { vapidKey, serviceWorkerRegistration });
}
